import { BenefitCategory } from '~/generated/api-clients-generated'
import { RxStore } from '~/stores/rx'

export interface DrugHighlight {
  key: string
  label: string
  icon: string
  priority: number
  categoryIds: readonly BenefitCategory[]
}

const DRUG_TRIGGERS: Readonly<Record<string, readonly string[]>> = {
  diabetes: [
    'metformin',
    'glucophage',
    'glipizide',
    'glucotrol',
    'glimepiride',
    'amaryl',
    'glyburide',
    'diabeta',
    'sitagliptin',
    'januvia',
    'empagliflozin',
    'jardiance',
    'dapagliflozin',
    'farxiga',
    'canagliflozin',
    'invokana',
    'pioglitazone',
    'actos',
    'semaglutide',
    'ozempic',
    'rybelsus',
    'dulaglutide',
    'trulicity',
    'liraglutide',
    'victoza',
    'exenatide',
    'byetta',
    'bydureon',
    'insulin glargine',
    'lantus',
    'basaglar',
    'toujeo',
    'insulin detemir',
    'levemir',
    'insulin degludec',
    'tresiba',
    'insulin lispro',
    'humalog',
    'admelog',
    'insulin aspart',
    'novolog',
    'fiasp',
    'insulin regular',
    'humulin r',
    'novolin r',
    'insulin nph',
    'humulin n',
    'novolin n',
    'tirzepatide',
    'mounjaro'
  ],
  lab: [
    'warfarin',
    'coumadin',
    'levothyroxine',
    'synthroid',
    'levoxyl',
    'liothyronine',
    'cytomel',
    'atorvastatin',
    'lipitor',
    'simvastatin',
    'zocor',
    'rosuvastatin',
    'crestor',
    'pravastatin',
    'pravachol',
    'metformin',
    'glucophage',
    'lisinopril',
    'zestril',
    'prinivil',
    'losartan',
    'cozaar',
    'furosemide',
    'lasix',
    'hydrochlorothiazide',
    'microzide',
    'spironolactone',
    'aldactone',
    'lithium',
    'lithobid',
    'digoxin',
    'lanoxin',
    'amiodarone',
    'cordarone',
    'pacerone',
    'phenytoin',
    'dilantin',
    'valproic acid',
    'depakote',
    'tacrolimus',
    'prograf',
    'methotrexate',
    'trexall',
    'cyclosporine',
    'sandimmune',
    'neoral',
    'apixaban',
    'eliquis',
    'ezetimibe',
    'zetia'
  ],
  cardiac: [
    'metoprolol',
    'lopressor',
    'toprol',
    'carvedilol',
    'coreg',
    'atenolol',
    'tenormin',
    'bisoprolol',
    'zebeta',
    'isosorbide mononitrate',
    'imdur',
    'isosorbide dinitrate',
    'isordil',
    'nitroglycerin',
    'nitrostat',
    'sacubitril',
    'valsartan',
    'entresto',
    'ivabradine',
    'corlanor',
    'hydralazine',
    'apresoline',
    'amiodarone',
    'cordarone',
    'digoxin',
    'lanoxin',
    'amlodipine',
    'norvasc',
    'clopidogrel',
    'plavix'
  ],
  pulmonary: [
    'albuterol',
    'proair',
    'ventolin',
    'proventil',
    'tiotropium',
    'spiriva',
    'umeclidinium',
    'incruse',
    'salmeterol',
    'serevent',
    'formoterol',
    'perforomist',
    'fluticasone',
    'advair',
    'breo',
    'budesonide',
    'symbicort',
    'pulmicort',
    'roflumilast',
    'daliresp',
    'montelukast',
    'singulair',
    'theophylline',
    'prednisone',
    'deltasone',
    'trelegy'
  ],
  mental: [
    'sertraline',
    'zoloft',
    'escitalopram',
    'lexapro',
    'citalopram',
    'celexa',
    'fluoxetine',
    'prozac',
    'paroxetine',
    'paxil',
    'duloxetine',
    'cymbalta',
    'venlafaxine',
    'effexor',
    'bupropion',
    'wellbutrin',
    'zyban',
    'mirtazapine',
    'remeron',
    'trazodone',
    'desyrel',
    'quetiapine',
    'seroquel',
    'aripiprazole',
    'abilify',
    'risperidone',
    'risperdal',
    'olanzapine',
    'zyprexa',
    'lithium',
    'lithobid',
    'eskalith',
    'lamotrigine',
    'lamictal',
    'valproic acid',
    'depakote',
    'buspirone',
    'buspar',
    'lorazepam',
    'ativan',
    'clonazepam',
    'klonopin',
    'donepezil',
    'aricept',
    'memantine',
    'namenda',
    'alprazolam',
    'xanax'
  ],
  dme: ['ipratropium', 'atrovent', 'nebulizer'],
  kidney: [
    'sevelamer',
    'renvela',
    'renagel',
    'cinacalcet',
    'sensipar',
    'darbepoetin',
    'aranesp',
    'epoetin',
    'epogen',
    'procrit',
    'sodium bicarbonate',
    'fludrocortisone',
    'florinef',
    'patiromer',
    'veltassa',
    'sodium zirconium',
    'lokelma'
  ],
  podiatry: ['pregabalin', 'lyrica', 'gabapentin', 'neurontin', 'duloxetine', 'cymbalta'],
  glaucoma: [
    'latanoprost',
    'xalatan',
    'bimatoprost',
    'lumigan',
    'travoprost',
    'travatan',
    'tafluprost',
    'zioptan',
    'timolol',
    'timoptic',
    'dorzolamide',
    'trusopt',
    'brinzolamide',
    'azopt',
    'brimonidine',
    'alphagan',
    'acetazolamide',
    'diamox',
    'netarsudil',
    'rhopressa',
    'cosopt',
    'combigan',
    'rocklatan'
  ],
  therapy: [
    'alendronate',
    'fosamax',
    'risedronate',
    'actonel',
    'zoledronic acid',
    'reclast',
    'denosumab',
    'prolia',
    'teriparatide',
    'forteo',
    'baclofen',
    'lioresal',
    'cyclobenzaprine',
    'flexeril',
    'methocarbamol',
    'robaxin'
  ]
}

// Priority 1 is reserved for flow-selected benefits. Drug highlights begin at 2.
const HIGHLIGHT_META: Readonly<Record<string, Omit<DrugHighlight, 'key'>>> = {
  diabetes: {
    label: 'Diabetes Care',
    icon: 'lucide:syringe',
    priority: 2,
    categoryIds: [BenefitCategory.DiabetesSuppliesAndServices]
  },
  lab: {
    label: 'Lab Services',
    icon: 'lucide:flask-conical',
    priority: 3,
    categoryIds: [
      BenefitCategory.MedicareCoveredLabServices,
      BenefitCategory.DiagnosticTestsLabsRadiologyAndXrays
    ]
  },
  cardiac: {
    label: 'Cardiac Rehab',
    icon: 'lucide:heart-pulse',
    priority: 4,
    categoryIds: [
      BenefitCategory.CardiacRehabServices,
      BenefitCategory.IntensiveCardiacRehabilitationServices
    ]
  },
  pulmonary: {
    label: 'Pulmonary Rehab',
    icon: 'lucide:wind',
    priority: 5,
    categoryIds: [BenefitCategory.PulmonaryRehabilitationServices]
  },
  mental: {
    label: 'Mental Health',
    icon: 'lucide:brain',
    priority: 6,
    categoryIds: [BenefitCategory.OutpatientMentalHealthCare, BenefitCategory.PsychiatricServices]
  },
  dme: {
    label: 'Medical Equipment',
    icon: 'wheelchair',
    priority: 7,
    categoryIds: [BenefitCategory.DurableMedicalEquipment]
  },
  kidney: {
    label: 'Kidney Care',
    icon: 'lucide:droplets',
    priority: 8,
    categoryIds: [BenefitCategory.RenalDialysis, BenefitCategory.KidneyDiseaseEducation]
  },
  podiatry: {
    label: 'Podiatry Care',
    icon: 'lucide:footprints',
    priority: 9,
    categoryIds: [BenefitCategory.PodiatryServices]
  },
  otc: {
    label: 'Over the Counter',
    icon: 'lucide:shopping-basket',
    priority: 10,
    categoryIds: [BenefitCategory.OverTheCounterItems]
  },
  glaucoma: {
    label: 'Glaucoma Screening',
    icon: 'lucide:eye',
    priority: 11,
    categoryIds: [BenefitCategory.GlaucomaScreening]
  },
  therapy: {
    label: 'Therapy Services',
    icon: 'lucide:activity',
    priority: 12,
    categoryIds: [
      BenefitCategory.PhysicalAndOrSpeechTherapyVisits,
      BenefitCategory.OccupationalTherapyVisits,
      BenefitCategory.ChiropracticServices
    ]
  }
}

export class DrugHighlightMap {
  static use() {
    const rxStore = RxStore.use()

    const drugDerivedHighlights = computed((): DrugHighlight[] => {
      const rxs = rxStore.rxs ?? []
      const names = rxs.map((rx) => (rx.drugName ?? '').toLowerCase())

      const triggered = new Set<string>()

      for (const [key, terms] of Object.entries(DRUG_TRIGGERS)) {
        if (names.some((name) => terms.some((term) => name.includes(term)))) {
          triggered.add(key)
        }
      }

      // OTC rule-based trigger: 4+ meds, or any diabetes/COPD/cardiac drug
      if (
        rxs.length >= 4 ||
        triggered.has('diabetes') ||
        triggered.has('pulmonary') ||
        triggered.has('cardiac')
      ) {
        triggered.add('otc')
      }

      // Podiatry: also triggered by any diabetes drug (neuropathy risk)
      if (triggered.has('diabetes')) {
        triggered.add('podiatry')
      }

      return [...triggered]
        .filter((key): key is keyof typeof HIGHLIGHT_META => key in HIGHLIGHT_META)
        .map((key) => ({ key, ...HIGHLIGHT_META[key] }))
        .sort((a, b) => a.priority - b.priority)
    })

    return { drugDerivedHighlights }
  }
}
